import type {
  CreateGiftCardBatchRequest,
  CreateGiftCardBatchResponse,
  GetGiftCardResponse,
  GiftCardBatchSummary,
  IssueGiftCardRequest,
  IssueGiftCardResponse,
  ListGiftCardBatchesResponse,
  ListGiftCardsQuery,
  ListGiftCardsResponse,
  RedeemGiftCardOnBehalfResponse,
  RevokeGiftCardResponse,
  UpdateGiftCardBatchStatusResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  closeGiftCardBatch,
  createGiftCardBatch,
  getGiftCard,
  issueGiftCard,
  listGiftCardBatches,
  listGiftCards,
  redeemOnBehalf,
  reopenGiftCardBatch,
  revokeGiftCard,
  type RedeemOnBehalfClientRequest,
  type RevokeGiftCardClientRequest,
} from './giftCards.ts';

/**
 * React Query wrappers for the gift-cards API.
 *
 * Phase 1 has NO list endpoint — every interaction starts with a code.
 * The query key namespace mirrors that:
 *
 *   - `['giftCards', code]` — single lookup
 *
 * Mutation invalidation map:
 *   - issueGiftCard      → no cache to invalidate (no list); the page
 *     surfaces the issued code from the mutation result directly.
 *   - revokeGiftCard     → invalidate `['giftCards', code]` so the
 *     looked-up panel re-fetches and shows the new revoked_at stamp.
 *   - redeemOnBehalf     → invalidate `['giftCards', code]` (lookup
 *     panel shows redeemed_at + redeemed_by_account_id post-success).
 */

export const GIFT_CARDS_NAMESPACE = ['giftCards'] as const;
export const GIFT_CARD_BATCHES_NAMESPACE = ['giftCardBatches'] as const;

export function giftCardQueryKey(code: string): readonly ['giftCards', string] {
  return ['giftCards', code] as const;
}

export function giftCardListQueryKey(
  query: ListGiftCardsQuery,
): readonly ['giftCards', 'list', ListGiftCardsQuery] {
  return ['giftCards', 'list', query] as const;
}

export function giftCardBatchesQueryKey(): readonly ['giftCardBatches'] {
  return GIFT_CARD_BATCHES_NAMESPACE;
}

export function useGiftCardBatches(): UseQueryResult<ListGiftCardBatchesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: giftCardBatchesQueryKey(),
    queryFn: ({ signal }) => listGiftCardBatches(fetchImpl, signal),
    retry: false,
  });
}

export function useGiftCardList(
  query: ListGiftCardsQuery,
): UseQueryResult<ListGiftCardsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: giftCardListQueryKey(query),
    queryFn: ({ signal }) => listGiftCards(query, fetchImpl, signal),
    retry: false,
  });
}

interface UseGiftCardOptions {
  /**
   * When false, the query stays idle (no fetch). The lookup form sets
   * this to true on submit and back to false on reset.
   */
  readonly enabled?: boolean;
}

export function useGiftCard(
  code: string | undefined,
  options: UseGiftCardOptions = {},
): UseQueryResult<GetGiftCardResponse> {
  const { fetchImpl } = useSession();
  const enabled =
    code !== undefined && code.length > 0 && (options.enabled ?? true);
  const queryOpts: UseQueryOptions<GetGiftCardResponse, Error> = {
    queryKey: giftCardQueryKey(code ?? ''),
    queryFn: ({ signal }) => {
      if (code === undefined || code.length === 0) {
        throw new Error('useGiftCard: code is required');
      }
      return getGiftCard(code, fetchImpl, signal);
    },
    enabled,
    retry: false,
  };
  return useQuery(queryOpts);
}

export function useIssueGiftCard(): UseMutationResult<
  IssueGiftCardResponse,
  Error,
  IssueGiftCardRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => issueGiftCard(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GIFT_CARDS_NAMESPACE });
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}

export function useCreateGiftCardBatch(): UseMutationResult<
  CreateGiftCardBatchResponse,
  Error,
  CreateGiftCardBatchRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createGiftCardBatch(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}

export function useCloseGiftCardBatch(): UseMutationResult<
  UpdateGiftCardBatchStatusResponse,
  Error,
  GiftCardBatchSummary['id']
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => closeGiftCardBatch(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}

export function useReopenGiftCardBatch(): UseMutationResult<
  UpdateGiftCardBatchStatusResponse,
  Error,
  GiftCardBatchSummary['id']
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => reopenGiftCardBatch(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}

export interface RevokeGiftCardVars {
  readonly code: string;
  readonly body: RevokeGiftCardClientRequest;
}

export function useRevokeGiftCard(): UseMutationResult<
  RevokeGiftCardResponse,
  Error,
  RevokeGiftCardVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }) => revokeGiftCard(code, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: giftCardQueryKey(vars.code) });
      void qc.invalidateQueries({ queryKey: GIFT_CARDS_NAMESPACE });
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}

export function useRedeemOnBehalf(): UseMutationResult<
  RedeemGiftCardOnBehalfResponse,
  Error,
  RedeemOnBehalfClientRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => redeemOnBehalf(body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: giftCardQueryKey(vars.code) });
      void qc.invalidateQueries({ queryKey: GIFT_CARDS_NAMESPACE });
      void qc.invalidateQueries({ queryKey: GIFT_CARD_BATCHES_NAMESPACE });
    },
  });
}
